import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {TranslateService} from "@ngx-translate/core";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {EFormsOrgService} from "../../../services/e-forms-org.service";
import {InformationComponent} from "../../../../../shared/modals/information/information.component";
import {EformDuplicableEntityService} from "../../../services/eform-duplicable-entity.service";
import {Group} from "../../../model/group";
import {first, map, tap} from 'rxjs/operators';
import {CanNavigateInStepperService} from '../../../../../shared/services/can-navigate-in-stepper.service';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {EformConditionsV2Service} from "../../../services/eform-conditions-v2.service";
import {EformsFieldCounterService} from '../../../services/eforms-field-counter.service';
import {EformsSubjectService} from "../../../services/eforms-subject.service";
import {DuplicableElementsIdSchemeEnum} from "../../../enum/duplicable-elements-id-scheme.enum";
import {EformDuplicableFieldsService} from "../../../services/eform-duplicable-fields.service";


@Component({
  selector: 'app-duplicable-element',
  templateUrl: './duplicable-element.component.html',
  styleUrls: ['./duplicable-element.component.scss']
})
export class DuplicableElementComponent implements OnInit, OnChanges, OnDestroy {
  private static instanceCounter = 0;
  private uniqueId: string;
  private subscriptions: Subscription[] = [];
  private previousActualIndex: number = -1; // Pour tracker les changements d'index

  @Input() duplicableElement: any;
  @Input() prefixId: string;
  @Input() isDeletable: boolean;
  @Input() displayedElementIndex: number;
  @Input() actualIndex: number
  @Output() addSomething = new EventEmitter();
  @Output() deleteSomething = new EventEmitter();
  @Input() infoBulle: string;
  @Input() elementLabel: string;
  @Input() elementId: string;
  @Input() type: string;
  @Output() loadDuplicableElement: EventEmitter<Group> = new EventEmitter();

  private tempButtonEl: HTMLElement;
  private dontSaveOnToggleAccordion: boolean = false;
  private loadDuplicableElementIsEmitted: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private isCurrentlyReloading: boolean = false;
  // 🔧 FIX 2026-02-11: Protection contre les double-clics sur le bouton "Ajouter"
  private isCreatingSomething: boolean = false;


  constructor(private elementRef: ElementRef, private translateService: TranslateService, private modalService: NgbModal,
              private eFormsOrgService: EFormsOrgService, private eformDuplicableEntityService: EformDuplicableEntityService,
              private canNavigateInStepperService: CanNavigateInStepperService,
              private eformsSubjectService: EformsSubjectService,
              private eformConditionsV2Service: EformConditionsV2Service,
              private eformsFieldCounterService: EformsFieldCounterService,
              private eformDuplicableFieldsService: EformDuplicableFieldsService,
              private cdr: ChangeDetectorRef,
              private ngZone: NgZone) {
    // Générer un ID unique pour cette instance
    this.uniqueId = `dup-elem-${DuplicableElementComponent.instanceCounter++}`;
  }

  ngAfterViewInit() {
    this.tempButtonEl = this.elementRef.nativeElement.querySelector('#' + this.getDuplicableElementId());
  }

  getDuplicableElementId()
    :
    string {
    const baseId = this.duplicableElement.id.includes('_') ? this.duplicableElement.id.split('_')[0] + '_' + this.actualIndex : this.duplicableElement.id;
    // Ajouter prefixId ET uniqueId pour garantir l'unicité absolue (utilisé uniquement pour l'attribut HTML id)
    return this.prefixId ? `${this.prefixId}-${baseId}-${this.uniqueId}` : `${baseId}-${this.uniqueId}`;
  }

  /**
   * Retourne l'ID "propre" pour l'affichage dans le titre (sans le suffixe technique uniqueId)
   */
  getDisplayId(): string {
    return this.duplicableElement.id.includes('_') ? this.duplicableElement.id.split('_')[0] + '_' + this.actualIndex : this.duplicableElement.id;
  }

  ngOnInit(): void {

    // Initialisation de l'Observable qui écoute les changements d'état d'expansion
    this.isExpanded$ = this.eformDuplicableEntityService.expanded$.pipe(
      map((expandedMap: Map<string, number>) => {
        // Récupération de l'index affiché depuis la Map
        let displayedIndex = expandedMap.get(this.type);
        // Si undefined, on récupère directement depuis le service
        if (displayedIndex === undefined) {
          displayedIndex = this.eformDuplicableEntityService.getDisplayedElementIndex(this.type);
        }
        return displayedIndex === this.actualIndex;
      }),
      tap(() => {
        // ✅ FIX NG0100: Utiliser markForCheck au lieu de detectChanges
        // detectChanges déclenche une vérification synchrone qui peut modifier l'état après la vérification
        // markForCheck marque le composant pour vérification au prochain cycle, évitant le conflit
        this.cdr.markForCheck();
      })
    );

    // Suite à la nouvelle mécanique de chargement des objets duplicable depuis le backend on charge 2 fois le front
    // La premiere fois pour avoir un init vide à persister et la deuxieme fois en ne chargeant qu'un objet duplicable à la fois pour la perf
    // 🔧 FIX : Conserver TOUS les IDs existants (duplicables ET non-duplicables) pour éviter la régénération incorrecte
    // On ne réinitialise QUE lors de la première instance (actualIndex === 0) pour le type courant
    // Cela permet de conserver les IDs des autres types d'éléments duplicables (CON, TEN, TPA, etc.)

    // ✅ CORRECTION 2025-12-10 : Réinitialiser les IDs d'un type donné SEULEMENT si l'élément n'a pas d'ID technique valide
    // Cela évite le problème où un élément dupliqué sans ID technique (NOID/EMPTY) génère l'ID suivant au lieu de recommencer à 1
    const currentElements = this.eformDuplicableEntityService.listDuplicableEntity.getValue().filter(e => e && e.id && e.id.split('-')[0] === this.type);
    const currentElement = currentElements?.[this.actualIndex];
    const hasNoValidTechnicalId = !currentElement ||
      currentElement.id?.includes('NOID') ||
      currentElement.id?.includes('EMPTY') ||
      currentElement._hasNoTechnicalId ||
      currentElement._hasEmptyTechnicalId;

    if (this.actualIndex === 0 || (hasNoValidTechnicalId && this.actualIndex > 0)) {
      // Premier élément de ce type OU élément sans ID technique valide : on filtre les IDs de CE type pour permettre la régénération correcte
      const existingIds = this.eformsSubjectService?.idSchemeListSubject?.getValue() || [];
      const idsToKeep = existingIds.filter(
        idScheme => idScheme != null && (
          // Garder les éléments non-duplicables
          !Object.values(DuplicableElementsIdSchemeEnum).includes(idScheme.split('-')[0]) ||
          // Garder les IDs des AUTRES types d'éléments duplicables
          idScheme.split('-')[0] !== this.type
        )
      );
      this.eformsSubjectService.setIdSchemeListSubject(idsToKeep);
    }
    // Si ce n'est pas le premier élément et qu'il a un ID technique valide, on ne touche pas à la liste

    // ⚠️ NE charger QUE si c'est la première fois et que c'est l'élément actuellement affiché
    const isAlreadyLoaded = this.eformDuplicableEntityService.isDuplicableElementLoaded(this.type, this.actualIndex);
    const displayedIndex = this.eformDuplicableEntityService.getDisplayedElementIndex(this.type);
    if (displayedIndex === this.actualIndex && !isAlreadyLoaded) {

      if (this.canNavigateInStepperService.avisLieModalite > 0) {
        if (!this.eformDuplicableEntityService.duplicableSectionAvisLieIsCreated.getValue()) {
          this.eformDuplicableEntityService.duplicableSectionAvisLieIsCreated.pipe(first(isEnded => isEnded === true)).subscribe(() => {
            this.reloadDuplicableElement(displayedIndex);
          })
        } else {
          this.reloadDuplicableElement(displayedIndex);
        }
      } else {
        this.reloadDuplicableElement(displayedIndex);
      }
    }
    //Inscription de l'élément duplicable pour pouvoir lancer la fonction toggleAccordion depuis le service
    this.subscriptions.push(
      this.eformDuplicableEntityService.registerDuplicableEntity(this.duplicableElement.id + '_' + this.actualIndex)
        .subscribe(index => this.toggleAccordionIfNeeded(index))
    );

    // 🔧 FIX 2026-02-20: S'abonner aux demandes de réouverture d'accordéon après suppression
    this.subscriptions.push(
      this.eformDuplicableEntityService.requestOpenAccordion$.subscribe(request => {
        if (request.type === this.type && request.index === this.actualIndex) {
          this.dontSaveOnToggleAccordion = true;
          this.toggleAccordion(this.actualIndex);
        }
      })
    );

    // 🔧 FIX 2026-02-20: Signaler que ce composant est prêt à recevoir des demandes d'ouverture
    this.eformDuplicableEntityService.notifyComponentReady(this.type, this.actualIndex);

    // Stocker l'index actuel pour détecter les changements dans ngOnChanges
    this.previousActualIndex = this.actualIndex;
  }

  /**
   * 🔧 FIX 2026-02-20: Détecter les changements d'actualIndex quand Angular réutilise le composant
   * Quand le trackBy permet la réutilisation, ngOnInit n'est pas rappelé mais ngOnChanges l'est
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['actualIndex'] && !changes['actualIndex'].firstChange) {
      const oldIndex = changes['actualIndex'].previousValue;
      const newIndex = changes['actualIndex'].currentValue;


      // Notifier le service que l'ancien index n'est plus valide pour ce composant
      if (oldIndex !== undefined && oldIndex !== newIndex) {
        this.eformDuplicableEntityService.notifyComponentDestroyed(this.type, oldIndex);
      }

      // Notifier le service que ce composant est maintenant prêt avec le nouvel index
      this.eformDuplicableEntityService.notifyComponentReady(this.type, newIndex);

      this.previousActualIndex = newIndex;
    }
  }

  /**
   * Cette fonction est activée à distance depuis la liste d'erreurs schematrons
   * si l'index de l'objet duplicable est différent de l'actuel, il faut l'ouvrir (et fermer l'autre).
   */
  private toggleAccordionIfNeeded(index: number) {
    const displayedIndex = this.eformDuplicableEntityService.getDisplayedElementIndex(this.type);
    if (index !== null && index >= 0 && index !== displayedIndex) {
      this.toggleAccordion(index);
    }
  }

  getEvent(event: Event) {
    event.stopPropagation();
    const modalRef = this.modalService.open(InformationComponent);
    modalRef.componentInstance.content = this.translateService.instant(this.infoBulle);
  }

  getAccordionTitle(): string {
    // ✅ FIX 2026-03-18: Gérer le cas où _label est undefined (ex: GR-LotResult)
    // Dans ce cas, on utilise un fallback basé sur l'ID ou le type de l'élément
    let title: string;
    if (this.duplicableElement._label) {
      title = this.translateService.instant(this.duplicableElement._label);
    } else {
      // Fallback: utiliser une traduction basée sur le type ou l'ID de l'élément
      title = this.getFallbackTitle();
    }

    let duplicableElementId = this.getDisplayId(); // Utiliser getDisplayId() pour l'affichage propre
    if (duplicableElementId) {
      title += " (" + this.translateService.instant(duplicableElementId) + ")";
    }
    if (title.includes("(GR-Organisations)") && this.actualIndex === 0) {
      title += " - ORG-0001 - Acheteur";
    }
    if (title.includes("(GR-Organisations") && this.actualIndex > 0) {
      const indexOrg: number = this.actualIndex + 1;
      let orgNumber: string = "0000".slice(indexOrg.toString().length) + indexOrg;
      title += " - ORG-" + orgNumber;

      if (this.actualIndex === 1 && this.eFormsOrgService.getNeedSecondOrganization()) {
        title += " - Organisation de recours";
      }
    }

    if(!title.includes("(GR-Organisations") && this.actualIndex > 0){
      title = title.slice(0, -1) + "_" + this.actualIndex + title.slice(-1);
    }

    return title;
  }

  /**
   * ✅ FIX 2026-03-18: Retourne un titre de fallback quand _label n'est pas défini
   * Utilisé pour les éléments duplicables comme GR-LotResult qui ont _captionFieldId mais pas _label
   */
  private getFallbackTitle(): string {
    // Mapping des types vers les libellés par défaut
    const typeToLabel: { [key: string]: string } = {
      'RES': 'Résultat de lot',
      'CON': 'Marché',
      'TEN': 'Offre',
      'TPA': 'Soumissionnaire',
      'ORG': 'Organisation',
      'LOT': 'Lot',
      'PAR': 'Partie'
    };

    // Utiliser le type si disponible
    if (this.type && typeToLabel[this.type]) {
      return typeToLabel[this.type];
    }

    // Fallback final: utiliser l'ID de l'élément ou un texte générique
    if (this.duplicableElement?.id) {
      // Essayer de traduire l'ID directement (ex: GR-LotResult)
      const translatedId = this.translateService.instant('eform-not-editable.group|name|' + this.duplicableElement.id);
      // Si la traduction existe (n'est pas égale à la clé), l'utiliser
      if (translatedId !== 'eform-not-editable.group|name|' + this.duplicableElement.id) {
        return translatedId;
      }
    }

    return 'Élément';
  }

  private reloadDuplicableElement(index: number) {
    // Empêcher le rechargement si déjà en cours
    if (this.isCurrentlyReloading) {
      return;
    }

    this.isCurrentlyReloading = true;

    // ✅ FIX 2026-03-04: Désactiver isLoadingAvisLie lors de la navigation entre lots
    // pour permettre la sauvegarde des valeurs lors du changement de lot
    this.eformConditionsV2Service.setLoadingAvisLie(false);

    // ✅ FIX 2026-03-05: Activer le chargement pour le type d'élément spécifique
    // Cela protège les champs conditionnels pendant le chargement initial
    // (ex: BT-3202-Contract pour CON, BT-5421-Lot pour LOT)
    this.eformDuplicableFieldsService.startLoadingDuplicableElement(this.type);

    // ✅ LEGACY: Conserver isLoadingLot pour compatibilité avec le code existant
    if (this.type === 'LOT') {
      this.eformConditionsV2Service.setLoadingLot(true);
    }

    // ✅ CORRECTION 2025-12-11: Passer le TYPE d'élément pour le tracking correct
    // Ceci bloque les rescans pendant le chargement
    this.eformConditionsV2Service.setReloadingDuplicableElement(true, this.type);

    // ⚠️ IMPORTANT: setDisplayedRealIndex sera appelé APRÈS le chargement des données
    // pour éviter que les rescans intermédiaires n'utilisent le mauvais index

    let avisId = this.eformDuplicableEntityService.currentAvisId.getValue();

    // ✅ CRITIQUE : Utiliser l'ID réel de l'élément depuis la liste, pas index + 1
    const currentElements = this.eformDuplicableEntityService.listDuplicableEntity.getValue().filter(e => e && e.id && e.id.split('-')[0] === this.type);

    if (!currentElements || index >= currentElements.length) {
      // ✅ FIX 2025-12-23: Pour un nouvel élément qui n'est pas encore dans la liste,
      // émettre un événement de chargement vide pour afficher les champs du formulaire
      this.isCurrentlyReloading = false;

      // Émettre un événement avec des données minimales pour déclencher l'affichage
      // ✅ FIX 2025-12-23: Émettre IMMÉDIATEMENT sans setTimeout pour éviter les race conditions
      const emptyData: any = { index: index, _isNewElement: true };
      this.loadDuplicableElement.emit(emptyData);
      this.loadDuplicableElementIsEmitted.next(true);
      this.eformDuplicableEntityService.isDataDuplicableEntityReady.next(true);
      this.eformConditionsV2Service.setDisplayedRealIndex(this.type, index);

      // ✅ FIX 2025-12-24: Mettre le flag à false APRÈS l'émission pour que les sous-composants
      // ne créent pas de doublons pendant leur initialisation
      // ✅ FIX 2026-03-05: Augmenter le délai pour CON, RES, TEN car leurs champs conditionnels
      // ont besoin de plus de temps pour s'initialiser complètement (500ms au lieu de 100ms)
      const delay = ['CON', 'RES', 'TEN'].includes(this.type) ? 500 : 100;
      setTimeout(() => {
        this.eformConditionsV2Service.setReloadingDuplicableElement(false, this.type);

        // ✅ FIX 2026-03-05: Désactiver le chargement pour le type spécifique
        this.eformDuplicableFieldsService.stopLoadingDuplicableElement(this.type);

        // ✅ LEGACY: Conserver isLoadingLot pour compatibilité
        if (this.type === 'LOT') {
          this.eformConditionsV2Service.setLoadingLot(false);
        }
      }, delay);
      return;
    }

    const element = currentElements[index];
    const elementRealId = element.id;

    // Vérifier si c'est un élément avec un ID temporaire (sans ID technique du backend)
    const isNoTechnicalIdElement = element._hasEmptyTechnicalId || element._hasNoTechnicalId ||
      elementRealId.includes('EMPTY') || elementRealId.includes('NOID');

    // Créer l'ID unique pour cet élément
    const elementId = `${this.type}_${index}`;

    // Reset du compteur pour cet élément (sauf pour UNK)
    if (this.type !== 'UNK') {
      this.eformsFieldCounterService.resetSubSectionCount(elementId);
    }

    if (isNoTechnicalIdElement) {
      // Pour les éléments sans ID technique, utiliser les données déjà disponibles
      // dans avisWithElementsDuplicable au lieu d'appeler le backend

      const originalIndex = element._originalIndex !== undefined ? element._originalIndex : index;
      const localData = this.getElementDataFromAvis(this.type, originalIndex);

      this.isCurrentlyReloading = false;

      if (localData) {
        this.eformDuplicableEntityService.markDuplicableElementAsLoaded(this.type, index);
        localData.index = index;

        // ✅ FIX 2025-12-23: Émettre IMMÉDIATEMENT pour éviter les race conditions
        // L'ancien setTimeout causait un problème où organization: [] était vide
        // lors de la navigation 1→3→2 car les contrôles n'étaient pas créés à temps.
        this.loadDuplicableElement.emit(localData);
        this.loadDuplicableElementIsEmitted.next(true);
        this.eformDuplicableEntityService.isDataDuplicableEntityReady.next(true);

        // ✅ Définir l'index réel APRÈS l'émission des données
        this.eformConditionsV2Service.setDisplayedRealIndex(this.type, index);

        // ✅ FIX Performance: Exécuter le timeout hors de la zone Angular pour éviter les violations
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this.ngZone.run(() => {
              this.eformConditionsV2Service.setReloadingDuplicableElement(false, this.type);
              this.eformConditionsV2Service.triggerCleanupAfterReload();

              // ✅ FIX 2026-03-05: Désactiver le chargement pour le type spécifique
              this.eformDuplicableFieldsService.stopLoadingDuplicableElement(this.type);

              // ✅ LEGACY: Conserver isLoadingLot pour compatibilité
              if (this.type === 'LOT') {
                this.eformConditionsV2Service.setLoadingLot(false);
              }
            });
          }, 500); // Réduit à 500ms - suffisant pour la stabilisation
        });

        if (this.type !== 'UNK') {
          this.eformsFieldCounterService.initializeSubSectionCount(elementId);
        }
      } else {
        // ✅ CORRECTION 2025-12-11: Passer le TYPE d'élément
        this.eformConditionsV2Service.setReloadingDuplicableElement(false, this.type);
      }
      return;
    }

    const idPart: number = parseInt(elementRealId.split('-')[1], 10);

    this.eformDuplicableEntityService.getDuplicableEntity(avisId, idPart, this.type).subscribe((data) => {
      this.isCurrentlyReloading = false;

      if (data) {

        // Marquer l'élément comme chargé dans le service
        this.eformDuplicableEntityService.markDuplicableElementAsLoaded(this.type, index);

        data.index = index;

        // ✅ FIX 2025-12-23: Émettre IMMÉDIATEMENT pour éviter les race conditions
        this.loadDuplicableElement.emit(data);
        this.loadDuplicableElementIsEmitted.next(true);
        this.eformDuplicableEntityService.isDataDuplicableEntityReady.next(true);

        // Définir l'index réel APRÈS l'émission des données
        this.eformConditionsV2Service.setDisplayedRealIndex(this.type, index);

        // FIX Performance: Exécuter hors zone Angular pour éviter les violations
        this.ngZone.runOutsideAngular(() => {
          requestAnimationFrame(() => {
            this.ngZone.run(() => {
              this.eformConditionsV2Service.setReloadingDuplicableElement(false, this.type);
              this.eformConditionsV2Service.triggerCleanupAfterReload();

              // ✅ FIX 2026-03-05: Désactiver le chargement pour le type spécifique
              // Utiliser un setTimeout pour laisser le temps aux champs conditionnels
              // de se créer AVANT que les conditions ne s'activent
              // ✅ FIX 2026-04-27: Augmenté de 500ms à 800ms pour Firefox avec 5+ lots
              setTimeout(() => {
                this.eformDuplicableFieldsService.stopLoadingDuplicableElement(this.type);

                // ✅ LEGACY: Conserver isLoadingLot pour compatibilité
                if (this.type === 'LOT') {
                  this.eformConditionsV2Service.setLoadingLot(false);
                }
              }, 800); // 800ms pour que tous les champs conditionnels se créent (Firefox-safe)
            });
          });
        });

        // Initialiser le compteur pour les statistiques (sauf pour UNK)
        if (this.type !== 'UNK') {
          this.eformsFieldCounterService.initializeSubSectionCount(elementId);
        }
      } else {
        // Si pas de données, libérer le verrou immédiatement
        // ✅ CORRECTION 2025-12-11: Passer le TYPE d'élément
        this.eformConditionsV2Service.setReloadingDuplicableElement(false, this.type);
      }
    });
  }

  /**
   * Calcule le nombre de champs attendus depuis les données de l'élément
   */
  private calculateExpectedFieldsFromData(data: any): number {
    // Cette méthode n'est plus utilisée avec le nouveau système
    return 0;
  }

  /**
   * Estime le nombre de champs basé sur les données et le type
   */
  private estimateFieldsCountFromData(data: any): number {
    // Cette méthode n'est plus utilisée avec le nouveau système
    return 0;
  }

  /**
   * Récupère les données d'un élément duplicable depuis avisWithElementsDuplicable.
   * Utilisé pour les éléments qui n'ont pas d'ID technique du backend.
   *
   * @param type Le type de l'élément (CON, TEN, RES, TPA)
   * @param originalIndex L'index original de l'élément dans le tableau
   * @returns Les données de l'élément ou null si non trouvé
   */
  private getElementDataFromAvis(type: string, originalIndex: number): any {
    const avisData = this.eformDuplicableEntityService.avisWithElementsDuplicable.getValue();
    if (!avisData?.jsonsaisieformulaire?.ann) return null;

    const ann = avisData.jsonsaisieformulaire.ann;

    // Mapping des types vers les noms des tableaux dans l'avis
    const typeToArrayName: { [key: string]: string } = {
      'CON': 'settledcontract',
      'TEN': 'lottender',
      'RES': 'lotresult',
      'TPA': 'tenderingparty'
    };

    const arrayName = typeToArrayName[type];
    if (!arrayName) return null;

    // Trouver le formulaire (peut être contractnotice, contractawardnotice, etc.)
    const formKey = Object.keys(ann).find(key =>
      key.includes('notice') && typeof ann[key] === 'object'
    );

    if (!formKey) return null;

    // Naviguer jusqu'au tableau d'éléments
    const pathParts = ['ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult'];
    let current = ann[formKey];

    for (const pathPart of pathParts) {
      if (!current) return null;
      current = current[pathPart];
    }

    if (!current || !Array.isArray(current[arrayName])) return null;

    const elementsArray = current[arrayName];
    if (originalIndex >= elementsArray.length) return null;

    return elementsArray[originalIndex];
  }

  toggleAccordion(index: number): void {
    const currentDisplayedIndex = this.eformDuplicableEntityService.getDisplayedElementIndex(this.type);

    if (currentDisplayedIndex === index) {
      // 🔧 FIX 2026-01-22: Sauvegarder les données AVANT de fermer l'accordéon
      // Sinon, si l'utilisateur ferme l'accordéon puis ouvre un autre élément,
      // les données de l'élément fermé ne seront pas sauvegardées
      if (currentDisplayedIndex !== -1) {
        this.eformDuplicableEntityService.saveCompleted$.pipe(
          first()
        ).subscribe(() => {
          this.closeAccordion();
        });
        this.eformDuplicableEntityService.requestImmediateSave$.next();
      } else {
        this.closeAccordion();
      }
    } else {
      // ✅ ROBUSTE 2025-12-16: Utiliser le nouveau système de sauvegarde synchrone
      // Au lieu de dépendre d'un debounce (timing non fiable), on utilise un système
      // de requête/réponse qui garantit que la sauvegarde est terminée avant de continuer
      if (!this.dontSaveOnToggleAccordion && currentDisplayedIndex !== -1) {

        // S'abonner à saveCompleted$ AVANT de déclencher la sauvegarde
        // Utiliser first() pour se désabonner automatiquement après la première émission
        this.eformDuplicableEntityService.saveCompleted$.pipe(
          first()
        ).subscribe(() => {
          this.proceedWithAccordionChange(index);
        });

        // Déclencher la sauvegarde immédiate (sans debounce)
        this.eformDuplicableEntityService.requestImmediateSave$.next();
      } else {
        // Pas de sauvegarde nécessaire, charger directement
        this.proceedWithAccordionChange(index);
      }
      this.dontSaveOnToggleAccordion = false;
    }
  }

  /**
   * Continue le changement d'accordéon après la sauvegarde (si nécessaire)
   */
  private proceedWithAccordionChange(index: number): void {
    this.openAccordion(index);

    if (this.actualIndex === index) {
      this.reloadDuplicableElement(index);
    }
  }

  openAccordion(index: number): void {
    this.eformDuplicableEntityService.setDisplayedElementIndex(this.type, index);
    // ⚠️ NE PAS appeler setDisplayedRealIndex ici !
    // L'index réel pour les flatFields doit être mis à jour APRÈS le chargement des données
    // dans reloadDuplicableElement(), sinon les données de l'élément actuel seront perdues
    // car le rescan utilisera le nouvel index avant que la sauvegarde ne soit faite.
  }

  closeAccordion(): void {
    this.eformDuplicableEntityService.closeAllAccordions(this.type);
  }

  // Observable pour le template qui écoute les changements d'état
  public isExpanded$: Observable<boolean>;

  removeSomething() {
    // ✅ FIX 2026-02-17: Sauvegarder les données AVANT la suppression
    // Quand on supprime le 1er élément, le 2ème se replie automatiquement.
    // Sans sauvegarde préalable, les données du 2ème élément sont perdues car
    // le repliage déclenche un rechargement qui écrase les valeurs non sauvegardées.
    const displayedIndex = this.eformDuplicableEntityService.getDisplayedElementIndex(this.type);

    if (displayedIndex !== -1) {
      // S'abonner à saveCompleted$ AVANT de déclencher la sauvegarde
      this.eformDuplicableEntityService.saveCompleted$.pipe(
        first()
      ).subscribe(() => {
        // Émettre l'événement de suppression APRÈS la sauvegarde
        this.deleteSomething.emit();
      });

      // Déclencher la sauvegarde immédiate (sans debounce)
      this.eformDuplicableEntityService.requestImmediateSave$.next();
    } else {
      // Pas d'élément affiché, émettre directement
      this.deleteSomething.emit();
    }
  }


  createSomething() {
    // 🔧 FIX 2026-02-11: Protection contre les double-clics et appels multiples
    if (this.isCreatingSomething) {
      return;
    }
    this.isCreatingSomething = true;

    const displayedIndex = this.eformDuplicableEntityService.getDisplayedElementIndex(this.type);
    if (displayedIndex != -1) {
      // ✅ FIX 2025-12-18: Utiliser le système de sauvegarde synchrone pour garantir
      // que les données de l'élément actuel sont sauvegardées AVANT d'ajouter un nouvel élément
      // Cela évite la perte de données lors du changement d'accordéon
      this.eformDuplicableEntityService.saveCompleted$.pipe(first()).subscribe(() => {
        this.addSomething.emit();
        // Réinitialiser le flag après un délai pour permettre le prochain ajout
        setTimeout(() => {
          this.isCreatingSomething = false;
        }, 1000);
      });

      // Déclencher la sauvegarde immédiate (sans debounce)
      this.eformDuplicableEntityService.requestImmediateSave$.next();
    } else {
      // Pas de sauvegarde nécessaire, ajouter directement
      this.addSomething.emit();
      // Réinitialiser le flag après un délai pour permettre le prochain ajout
      setTimeout(() => {
        this.isCreatingSomething = false;
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    // 🔧 FIX 2026-02-20: Signaler que ce composant n'est plus disponible
    this.eformDuplicableEntityService.notifyComponentDestroyed(this.type, this.actualIndex);

    // Nettoyer les abonnements
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
